import { zodResolver } from "@hookform/resolvers/zod";
import React, { useContext, useEffect, useMemo } from "react";
import { useFieldArray, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";

import { generateDefaultValues, schema, Schema } from "./schema";
import useRefreshLocationData, {
  RefreshType,
} from "../../../../containers/FacilityDetailsPage/hooks/useRefreshLocationData";
import { ConfirmCloseContext } from "../../../../contexts/confirmClose";
import { LocationData } from "../../../../models/LocationData";
import DataSvc from "../../../../services/dataSvc";
import { HookForm } from "../../../HookForm/HookForm";
import { Input } from "../../../HookForm/Input";

import "./styles.scss";

export interface IAddressSectionProps {
  locationData: LocationData;
  accordionEventKey: string;
}

export const AddressSection = (props: IAddressSectionProps) => {
  const { locationData, accordionEventKey } = props;

  const { t: _t } = useTranslation();
  const t = (key: string, params?: any) =>
    _t(`facilityDetailsPage.modalEditProfile.addressForm.${key}`, params);

  const refresh = useRefreshLocationData(locationData.id.toString());

  const defaultValues = useMemo(() => {
    return generateDefaultValues(locationData);
  }, [locationData]);

  const form = useForm<Schema>({
    resolver: zodResolver(schema),
    defaultValues,
  });

  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: "addressLines" as const,
  });

  // Reset form when locationData changes
  useEffect(() => {
    form.reset(defaultValues);
  }, [defaultValues, form]);

  const onSubmit = async (data: Schema) => {
    const payload = {
      location: {
        storefrontAddress: {
          regionCode: data.regionCode || undefined,
          postalCode: data.postalCode || undefined,
          administrativeArea: data.administrativeArea || undefined,
          addressLines:
            data.addressLines
              ?.map((item) => item.line)
              .filter((line) => line.trim() !== "") || undefined,
        },
      },
    };
    try {
      await DataSvc.updateLocationProfile(locationData.id, payload);
      toast.success(t("success"));
      refresh(RefreshType.LOCATION);
      form.reset(data);
    } catch (error) {
      // Do nothing, axios will handle the error
    }
  };

  const { register, unregister } = useContext(ConfirmCloseContext);

  useEffect(() => {
    if (form.formState.isDirty) {
      register({
        key: accordionEventKey,
        message: "dirty",
        onCloseCallback: form.reset,
      });
    } else {
      unregister(accordionEventKey);
    }
  }, [form.formState.isDirty, register, unregister, accordionEventKey, form]);

  return (
    <HookForm onSubmit={onSubmit} form={form} submitButtonLabel={t("update")}>
      <div className="address-section">
        <div className="mb-3">
          <label>{t("regionCode")}</label>
          <Input name="regionCode" placeholder={t("regionCode_placeholder")} />
        </div>

        <div className="mb-3">
          <label>{t("postalCode")}</label>
          <Input name="postalCode" placeholder={t("postalCode_placeholder")} />
        </div>

        <div className="mb-3">
          <label>{t("administrativeArea")}</label>
          <Input name="administrativeArea" />
        </div>

        <div className="mb-3">
          <label>{t("locality")}</label>
          <Input
            name="locality"
            placeholder={t("locality_placeholder")}
            disabled
          />
        </div>

        <div className="mb-3">
          <label>{t("sublocality")}</label>
          <Input
            name="sublocality"
            placeholder={t("sublocality_placeholder")}
            disabled
          />
        </div>

        <div className="mb-3">
          <label>{t("addressLines")}</label>
          {fields.map((field, index) => (
            <div key={field.id} className="subcategory-row">
              <div className="subcategory-row-input-container">
                <Input
                  name={`addressLines.${index}.line`}
                  placeholder={t("addressLine_placeholder", {
                    number: index + 1,
                  })}
                />
              </div>
              {fields.length > 1 && (
                <button
                  type="button"
                  className="btn btn-border btn-border-red"
                  onClick={() => remove(index)}
                >
                  {t("remove")}
                </button>
              )}
            </div>
          ))}
          <button
            type="button"
            className="btn btn-border add-address-line-btn"
            onClick={() => append({ line: "" })}
          >
            {t("add_address_line")}
          </button>
        </div>

        <p className="text-muted">{t("description")}</p>
      </div>
    </HookForm>
  );
};
